import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

// `dayjs.utc` is used to parse the stored UTC timestamps
dayjs.extend(utc)

/**
 * 🔄 Registration Period Status Interface
 *
 * Defines the structure for registration period status information
 */
export interface RegistrationPeriodStatus {
  /** Current status of the registration period */
  readonly status: 'before' | 'active' | 'after'
  /** Whether the current time is before the start date */
  readonly isBeforeStart: boolean
  /** Whether the current time is after the end date */
  readonly isAfterEnd: boolean
  /** Human-readable message describing the current status */
  readonly message: string
}

/** Shown while a registration's start instant is still in the future. */
export const REGISTRATION_NOT_OPEN_MESSAGE =
  'Registration is not currently open. Please check back later for updates.'

/** Shown once a registration's end instant has passed. */
export const REGISTRATION_CLOSED_MESSAGE =
  'Registration is closed. Please check back next season for updates.'

const ACTIVE_STATUS: RegistrationPeriodStatus = Object.freeze({
  status: 'active',
  isBeforeStart: false,
  isAfterEnd: false,
  message: '',
})

const BEFORE_STATUS: RegistrationPeriodStatus = Object.freeze({
  status: 'before',
  isBeforeStart: true,
  isAfterEnd: false,
  message: REGISTRATION_NOT_OPEN_MESSAGE,
})

const AFTER_STATUS: RegistrationPeriodStatus = Object.freeze({
  status: 'after',
  isBeforeStart: false,
  isAfterEnd: true,
  message: REGISTRATION_CLOSED_MESSAGE,
})

/**
 * 🔄 getRegistrationPeriodStatus: Determines if a registration is open based on date range
 *
 * The window is compared as absolute instants. `starts_at` / `ends_at` are stored
 * as UTC timestamps that already encode the wall-clock the admin picked in the
 * competition account's timezone (see `registrationDateTimeToUtcIso`), so the
 * offset is baked in and no further timezone handling is needed here — the
 * window opens and closes at the same moment for every visitor, wherever they are.
 *
 * Both bounds are inclusive: a registration is active from the start instant
 * through the end instant.
 *
 * **Note**: an earlier version discarded the time and treated the UTC calendar
 * date as a whole day in the *viewer's* local timezone. That predated the setup
 * form collecting a time, and it both opened registrations late and kept them
 * open well past their configured close.
 *
 * @param startsAtIso - ISO-8601 date string (UTC) when registration starts
 * @param endsAtIso - ISO-8601 date string (UTC) when registration ends
 * @param now - Current date/time for comparison (defaults to now)
 * @returns RegistrationPeriodStatus object with status and helper flags
 *
 * @example
 * ```typescript
 * // Window is 2026-08-08T01:23Z → 2026-08-10T00:14Z, now is inside it
 * const status = getRegistrationPeriodStatus(
 *   '2026-08-08T01:23:00.000Z',
 *   '2026-08-10T00:14:00.000Z'
 * )
 * // Returns: { status: 'active', isBeforeStart: false, isAfterEnd: false, message: '' }
 * ```
 */
export function getRegistrationPeriodStatus(
  startsAtIso: string | null | undefined,
  endsAtIso: string | null | undefined,
  now: dayjs.Dayjs = dayjs()
): RegistrationPeriodStatus {
  // No dates configured means the registration is not date-gated
  if (!startsAtIso || !endsAtIso) {
    return ACTIVE_STATUS
  }

  const startUtc = dayjs.utc(startsAtIso)
  const endUtc = dayjs.utc(endsAtIso)

  // A malformed date is a data problem, not a reason to lock visitors out
  if (!startUtc.isValid() || !endUtc.isValid()) {
    return ACTIVE_STATUS
  }

  if (now.isBefore(startUtc)) {
    return BEFORE_STATUS
  }

  if (now.isAfter(endUtc)) {
    return AFTER_STATUS
  }

  return ACTIVE_STATUS
}
